import React, { useState, useEffect, useCallback, useRef } from 'react';
import SvgIcon from '../SvgIcon';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Isolated PDF preview frame that loads the PDF via fetch with Accept: application/pdf
 * and renders a Blob object URL. This prevents Create React App dev-server's
 * connect-history-api-fallback from intercepting iframe navigation and serving index.html (Login).
 */
const PdfPreviewFrame = ({ templateId, title, isModal = false, userRole = 'Admin' }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let createdUrl = null;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/admin/certificate-templates/${templateId}/preview`, {
          headers: {
            'Accept': 'application/pdf',
            'x-admin-role': userRole
          }
        });
        if (!res.ok) {
          throw new Error(`Server returned HTTP ${res.status}`);
        }
        const blob = await res.blob();
        if (isMounted) {
          createdUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          setBlobUrl(createdUrl);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading template preview:', err);
          setError(err.message || 'Failed to load preview');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (templateId) {
      loadPdf();
    }

    return () => {
      isMounted = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [templateId, userRole]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b', gap: '8px', background: '#f8fafc' }}>
        <div style={{ width: '22px', height: '22px', border: '3px solid #cbd5e1', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Loading certificate preview...</span>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ef4444', gap: '6px', padding: '1rem', textAlign: 'center', background: '#fef2f2' }}>
        <SvgIcon name="alertTriangle" size={22} color="#ef4444" />
        <span style={{ fontSize: '0.825rem', fontWeight: 700 }}>Unable to render preview</span>
        <span style={{ fontSize: '0.725rem', color: '#64748b' }}>{error || 'PDF template file not found.'}</span>
      </div>
    );
  }

  const srcUrl = isModal
    ? blobUrl
    : `${blobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;

  return (
    <iframe
      src={srcUrl}
      title={title}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        pointerEvents: isModal ? 'auto' : 'none',
        display: 'block',
        background: '#ffffff'
      }}
    />
  );
};

export const CertificateTemplatesManager = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Modal states
  const [previewModal, setPreviewModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // { template, name, description, isActive }
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ templateCode: '', templateName: '', description: '', file: null });

  // Replacing PDF state
  const [replaceTarget, setReplaceTarget] = useState(null); // template object
  const [isSubmitting, setIsSubmitting] = useState(false);
  const replaceFileInputRef = useRef(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/certificate-templates`, {
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin'
        }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setTemplates(data.data || []);
      } else {
        showToast('error', data.message || 'Failed to load certificate templates.');
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Network error connecting to certificate service.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Handle file selection for replacing an existing template's PDF
  const handleReplaceFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceTarget) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showToast('error', 'Please upload a valid PDF file (.pdf).');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('templateFile', file);
    formData.append('templateId', replaceTarget.TemplateID);

    try {
      const res = await fetch(`${API_BASE}/api/admin/certificate-templates/upload`, {
        method: 'POST',
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin'
        },
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast('success', data.message || `Template "${replaceTarget.TemplateCode}" PDF template file replaced successfully!`);
        setReplaceTarget(null);
        fetchTemplates();
      } else {
        showToast('error', data.message || 'Failed to replace template PDF.');
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to upload replacement PDF.');
    } finally {
      setIsSubmitting(false);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  // Handle adding brand-new template
  const handleAddNewTemplate = async (e) => {
    e.preventDefault();
    if (!addForm.templateCode.trim() || !addForm.templateName.trim() || !addForm.file) {
      showToast('error', 'Please fill in Template Code, Name, and select a PDF file.');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('templateFile', addForm.file);
    formData.append('templateCode', addForm.templateCode.trim().toUpperCase());
    formData.append('templateName', addForm.templateName.trim());
    formData.append('description', addForm.description.trim());

    try {
      const res = await fetch(`${API_BASE}/api/admin/certificate-templates/upload`, {
        method: 'POST',
        headers: {
          'x-admin-role': user?.role || user?.Role || 'Admin'
        },
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast('success', data.message || 'New template registered successfully!');
        setIsAddModalOpen(false);
        setAddForm({ templateCode: '', templateName: '', description: '', file: null });
        fetchTemplates();
      } else {
        showToast('error', data.message || 'Failed to create new template.');
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Network error registering template.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle updating template metadata
  const handleSaveEditTemplate = async (e) => {
    e.preventDefault();
    if (!editModal) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/certificate-templates/${editModal.template.TemplateID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-role': user?.role || user?.Role || 'Admin'
        },
        body: JSON.stringify({
          templateName: editModal.name.trim(),
          description: editModal.description.trim(),
          isActive: editModal.isActive
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast('success', 'Template details updated successfully.');
        setEditModal(null);
        fetchTemplates();
      } else {
        showToast('error', data.message || 'Failed to update template.');
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to update template details.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  };

  return (
    <div style={{ textAlign: 'left', width: '100%', boxSizing: 'border-box' }}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          padding: '0.75rem 1.25rem',
          borderRadius: '8px',
          background: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: '#ffffff',
          fontWeight: 600,
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={18} />
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Hidden file input for Replace PDF */}
      <input
        type="file"
        ref={replaceFileInputRef}
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={handleReplaceFileChange}
      />

      {/* Templates Section Card */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.25rem 1.5rem',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SvgIcon name="fileText" size={20} color="#4f46e5" />
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
              Certificate Templates
            </h3>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsAddModalOpen(true)}
          style={{
            padding: '0.55rem 1.15rem',
            background: '#4f46e5',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)',
            transition: 'background 0.2s'
          }}
        >
          <SvgIcon name="plus" size={15} />
          <span>Add New Template</span>
        </button>
      </div>

      {/* Loading state */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem 2rem', color: '#64748b', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
          <p style={{ margin: 0, fontWeight: 600 }}>Loading certificate templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 2rem', background: '#ffffff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
          <SvgIcon name="fileText" size={36} color="#94a3b8" />
          <h4 style={{ margin: '0.75rem 0 0.25rem', color: '#1e293b' }}>No Certificate Templates Found</h4>
          <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>Register a template to start issuing completion certificates.</p>
        </div>
      ) : (
        /* Template List Table */
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr 240px 130px 100px 190px',
            padding: '0.85rem 1.25rem',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            gap: '12px'
          }}>
            <div>Code</div>
            <div>Certificate Name</div>
            <div>PDF Template File</div>
            <div>Course Usage</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>

          <div>
            {templates.map((tpl, idx) => (
              <div
                key={tpl.TemplateID}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr 240px 130px 100px 190px',
                  alignItems: 'center',
                  padding: '1rem 1.25rem',
                  borderBottom: idx < templates.length - 1 ? '1px solid #f1f5f9' : 'none',
                  gap: '12px',
                  background: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                }}
              >
                {/* Code */}
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    background: tpl.TemplateCode === 'RCC' ? '#dcfce7' : '#e0e7ff',
                    color: tpl.TemplateCode === 'RCC' ? '#166534' : '#3730a3',
                    letterSpacing: '0.5px'
                  }}>
                    {tpl.TemplateCode}
                  </span>
                  {tpl.IsDefault === 1 && (
                    <div style={{ marginTop: '4px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>
                        ★ Default
                      </span>
                    </div>
                  )}
                </div>

                {/* Certificate Name (Click to Preview) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setPreviewModal(tpl)}
                    title="Click to view full live master certificate preview"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#1d4ed8',
                      fontSize: '0.925rem',
                      fontWeight: 700
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    <span>{tpl.TemplateName}</span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: '#eff6ff',
                      color: '#2563eb',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <SvgIcon name="eye" size={11} color="#2563eb" />
                      <span>Preview</span>
                    </span>
                  </button>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.35, maxWidth: '520px' }}>
                    {tpl.Description || 'No description provided.'}
                  </p>
                </div>

                {/* Master PDF File */}
                <div style={{ fontSize: '0.78rem', color: '#475569' }}>
                  {tpl.fileExists ? (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, color: '#0f172a' }}>
                        <SvgIcon name="fileText" size={14} color="#64748b" />
                        <span style={{ wordBreak: 'break-all' }}>{tpl.PdfFileName}</span>
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.72rem', marginTop: '2px' }}>
                        {formatFileSize(tpl.fileSize)}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#dc2626', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <SvgIcon name="alertTriangle" size={14} color="#dc2626" />
                      <span>File missing from disk</span>
                    </div>
                  )}
                </div>

                {/* Course Usage */}
                <div>
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: '#f1f5f9',
                    color: '#334155'
                  }}>
                    {tpl.courseUsageCount} course{tpl.courseUsageCount !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Status */}
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    background: tpl.IsActive ? '#ecfdf5' : '#f1f5f9',
                    color: tpl.IsActive ? '#047857' : '#64748b'
                  }}>
                    {tpl.IsActive ? '● Active' : '○ Inactive'}
                  </span>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setReplaceTarget(tpl);
                      if (replaceFileInputRef.current) replaceFileInputRef.current.click();
                    }}
                    disabled={isSubmitting}
                    style={{
                      padding: '0.45rem 0.75rem',
                      background: '#f0fdf4',
                      color: '#166534',
                      border: '1px solid #bbf7d0',
                      borderRadius: '6px',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'background 0.2s'
                    }}
                    title="Upload new PDF file to replace this template file"
                  >
                    <SvgIcon name="refresh" size={12} color="#166534" />
                    <span>Replace PDF</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditModal({ template: tpl, name: tpl.TemplateName, description: tpl.Description || '', isActive: !!tpl.IsActive })}
                    style={{
                      padding: '0.45rem 0.75rem',
                      background: '#f8fafc',
                      color: '#334155',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Edit name, description, and status"
                  >
                    <SvgIcon name="edit" size={12} color="#475569" />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modal: Full Live Preview ── */}
      {previewModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            backdropFilter: 'blur(3px)'
          }}
          onClick={() => setPreviewModal(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '95vw',
              maxWidth: '960px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#0f172a',
              color: '#ffffff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SvgIcon name="fileText" size={18} color="#818cf8" />
                <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                  {previewModal.TemplateName} ({previewModal.TemplateCode}) - Live Preview
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModal(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <SvgIcon name="x" size={20} color="#ffffff" />
              </button>
            </div>

            <div style={{ flex: 1, width: '100%', height: '100%', background: '#334155' }}>
              <PdfPreviewFrame
                templateId={previewModal.TemplateID}
                title={`Full Preview of ${previewModal.TemplateName}`}
                isModal={true}
                userRole={user?.role || user?.Role || 'Admin'}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Add New Certificate Template ── */}
      {isAddModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            backdropFilter: 'blur(3px)'
          }}
          onClick={() => !isSubmitting && setIsAddModalOpen(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '520px',
              padding: '1.75rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              textAlign: 'left'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SvgIcon name="plus" size={18} color="#4f46e5" />
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                  Add Certificate Template
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isSubmitting}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <SvgIcon name="x" size={18} />
              </button>
            </div>

            <form onSubmit={handleAddNewTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  Template Code * <span style={{ fontWeight: 400, color: '#64748b' }}>(e.g. MCMC, RCC)</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. MCMC or RCC"
                  value={addForm.templateCode}
                  onChange={(e) => setAddForm({ ...addForm, templateCode: e.target.value.toUpperCase() })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  Template Name * <span style={{ fontWeight: 400, color: '#64748b' }}>(Display name)</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. BlackBerry CCoE Standard Certificate"
                  value={addForm.templateName}
                  onChange={(e) => setAddForm({ ...addForm, templateName: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  Description <span style={{ fontWeight: 400, color: '#64748b' }}>(Optional)</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Official completion certificate for MCMC / RCC courses."
                  value={addForm.description}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  PDF Template File * <span style={{ fontWeight: 400, color: '#64748b' }}>(.pdf only)</span>
                </label>
                <input
                  type="file"
                  required
                  accept=".pdf"
                  onChange={(e) => setAddForm({ ...addForm, file: e.target.files?.[0] || null })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', borderRadius: '8px', border: '1px dashed #cbd5e1', background: '#f8fafc', fontSize: '0.875rem' }}
                />
                <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Ensure your PDF has standard text fields (<code>NAME</code>, <code>NAME OF COURSE</code>, <code>DATE #1</code>, <code>DATE #3</code>, <code>Text1</code>, <code>DATE #2</code>) or default centered layout.
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={isSubmitting}
                  style={{ padding: '0.65rem 1rem', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ padding: '0.65rem 1.25rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  {isSubmitting ? 'Uploading...' : 'Register Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Edit Template Metadata ── */}
      {editModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            backdropFilter: 'blur(3px)'
          }}
          onClick={() => !isSubmitting && setEditModal(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '480px',
              padding: '1.75rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              textAlign: 'left'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                Edit Template Details ({editModal.template.TemplateCode})
              </h3>
              <button
                type="button"
                onClick={() => setEditModal(null)}
                disabled={isSubmitting}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <SvgIcon name="x" size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  Template Display Name *
                </label>
                <input
                  type="text"
                  required
                  value={editModal.name}
                  onChange={(e) => setEditModal({ ...editModal, name: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                  Description
                </label>
                <textarea
                  rows={3}
                  value={editModal.description}
                  onChange={(e) => setEditModal({ ...editModal, description: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', resize: 'vertical' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editModal.isActive}
                  onChange={(e) => setEditModal({ ...editModal, isActive: e.target.checked })}
                />
                <span>Active (Available for Course Assignment)</span>
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  disabled={isSubmitting}
                  style={{ padding: '0.65rem 1rem', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ padding: '0.65rem 1.25rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateTemplatesManager;
